import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase/config";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { useToast } from "../../components/ui/use-toast";
import { useAutoDeployment } from "../../hooks/useAutoDeployment";
import EditorWrapper from "../../components/EditorWrapper";
import {
  Loader2,
  PenLine,
  User,
  FileText,
  Save,
  ImageIcon,
  X,
  Calendar,
} from "lucide-react";
import { Skeleton } from "../../components/ui/skeleton";
import { motion } from "framer-motion";

interface BlogData {
  title: string;
  description: string;
  author: string;
  contentHtml: string;
  thumbnailUrl: string;
  publishDate?: string;
}

const EditBlog = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { triggerDeployment } = useAutoDeployment();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<BlogData>({
    title: "",
    description: "",
    author: "",
    contentHtml: "",
    thumbnailUrl: "",
    publishDate: new Date().toISOString().split("T")[0],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    const fetchBlog = async () => {
      if (!id) return;

      try {
        const docRef = doc(db, "BlogsWeb", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data() as BlogData & {
            createdAt?: any;
            publishDate?: any;
          };

          // Handle publishDate - use existing publishDate or convert createdAt to date string
          let publishDateString = new Date().toISOString().split("T")[0];
          if (data.publishDate) {
            // If publishDate exists, convert it to string
            const publishDate = data.publishDate.toDate
              ? data.publishDate.toDate()
              : new Date(data.publishDate);
            publishDateString = publishDate.toISOString().split("T")[0];
          } else if (data.createdAt) {
            // Fallback to createdAt if publishDate doesn't exist
            const createdDate = data.createdAt.toDate
              ? data.createdAt.toDate()
              : new Date(data.createdAt);
            publishDateString = createdDate.toISOString().split("T")[0];
          }

          setFormData({
            title: data.title,
            description: data.description,
            author: data.author,
            contentHtml: data.contentHtml,
            thumbnailUrl: data.thumbnailUrl,
            publishDate: publishDateString,
          });

          if (data.thumbnailUrl) {
            setThumbnailPreview(data.thumbnailUrl);
          }
        } else {
          toast({
            variant: "destructive",
            title: "Error",
            description: "Blog post not found",
          });
          navigate("/admin/blogs");
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to fetch blog post",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlog();
  }, [id, navigate, toast]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith("image/")) {
      toast({
        variant: "destructive",
        title: "Invalid File",
        description: "Please upload an image file",
      });
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "File Too Large",
        description: "Image size should be less than 5MB",
      });
      return;
    }

    setUploadingImage(true);

    try {
      // Create a unique file name
      const fileName = `${Date.now()}-${file.name}`;
      const storageRef = ref(storage, `blogsWeb/${fileName}`);

      // Upload the file
      await uploadBytes(storageRef, file);

      // Get the download URL
      const downloadUrl = await getDownloadURL(storageRef);

      // Set the preview and save the URL
      setThumbnailPreview(URL.createObjectURL(file));
      setFormData((prev) => ({ ...prev, thumbnailUrl: downloadUrl }));

      toast({
        title: "Success",
        description: "Thumbnail uploaded successfully",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Upload Failed",
        description: "Failed to upload thumbnail. Please try again.",
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const removeThumbnail = () => {
    setThumbnailPreview(null);
    setFormData((prev) => ({ ...prev, thumbnailUrl: "" }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    if (
      !formData.title ||
      !formData.description ||
      !formData.contentHtml ||
      !formData.thumbnailUrl ||
      !formData.publishDate
    ) {
      toast({
        variant: "destructive",
        title: "Missing Fields",
        description:
          "Please fill in all required fields including thumbnail and publish date",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const docRef = doc(db, "BlogsWeb", id);

      // Convert the publish date to a Date object
      const publishDateTime = new Date(formData.publishDate!);
      publishDateTime.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues

      await updateDoc(docRef, {
        title: formData.title,
        description: formData.description,
        author: formData.author,
        contentHtml: formData.contentHtml,
        thumbnailUrl: formData.thumbnailUrl,
        publishDate: publishDateTime,
        createdAt: publishDateTime, // Update createdAt to match publishDate
        updatedAt: new Date(),
      });

      toast({
        title: "Success",
        description: "Blog post updated successfully! 🎉",
      });

      // Auto-trigger deployment after successful blog update
      try {
        console.log("🚀 Triggering auto-deployment for updated blog...");
        const deployResult = await triggerDeployment({
          id: id!,
          title: formData.title,
          action: "updated",
        });

        if (deployResult.success) {
          toast({
            title: "Deployment Triggered! 🚀",
            description: "Your updated blog post will be live in 2-3 minutes.",
          });
        } else {
          toast({
            title: "Blog Updated ✅",
            description:
              "Blog updated successfully, but auto-deployment failed. You can trigger it manually.",
            variant: "default",
          });
        }
      } catch (deployError) {
        console.error("Auto-deployment failed:", deployError);
        toast({
          title: "Blog Updated ✅",
          description:
            "Blog updated successfully! Auto-deployment encountered an issue, but your changes are saved.",
          variant: "default",
        });
      }

      navigate("/admin/blogs");
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update blog post. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditorChange = useCallback((html: string) => {
    setFormData((prev) => ({ ...prev, contentHtml: html }));
  }, []);

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto p-6 space-y-8">
        <Skeleton className="h-8 w-1/4" />
        <div className="space-y-6 bg-white rounded-xl p-6 shadow-sm border border-misty-sage/20">
          <Skeleton className="h-[200px] w-full" /> {/* Thumbnail skeleton */}
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-[500px] w-full" />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-5xl mx-auto p-6 space-y-8"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-forest-green/10 flex items-center justify-center">
            <PenLine className="h-5 w-5 text-forest-green" />
          </div>
          <h1 className="font-heading text-2xl text-forest-green">
            Edit Blog Post
          </h1>
        </div>
        <Button
          variant="outline"
          className="border-forest-green text-forest-green hover:bg-forest-green/5 transition-colors duration-300"
          onClick={() => navigate("/admin/blogs")}
        >
          Cancel
        </Button>
      </div>

      <motion.form
        onSubmit={handleSubmit}
        className="space-y-6 bg-white rounded-xl p-6 shadow-sm border border-misty-sage/20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="space-y-6">
          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <label
              htmlFor="thumbnail"
              className="flex items-center gap-2 text-sm font-medium text-forest-green"
            >
              <ImageIcon className="h-4 w-4" />
              Blog Thumbnail
            </label>
            <div className="relative">
              <input
                type="file"
                id="thumbnail"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              {thumbnailPreview ? (
                <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-misty-sage/20">
                  <img
                    src={thumbnailPreview}
                    alt="Thumbnail preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={removeThumbnail}
                    className="absolute top-2 right-2 p-1 rounded-full bg-white/80 hover:bg-white transition-colors"
                  >
                    <X className="h-4 w-4 text-forest-green" />
                  </button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full aspect-video border-dashed border-2 border-misty-sage hover:border-forest-green transition-colors duration-300"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <ImageIcon className="mr-2 h-4 w-4" />
                      Upload Thumbnail
                    </>
                  )}
                </Button>
              )}
            </div>
          </motion.div>

          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <label
              htmlFor="title"
              className="flex items-center gap-2 text-sm font-medium text-forest-green"
            >
              <FileText className="h-4 w-4" />
              Blog Title
            </label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, title: e.target.value }))
              }
              placeholder="Enter an engaging title for your blog"
              className="border-misty-sage focus:border-forest-green transition-colors duration-300 placeholder:text-forest-green/30 text-forest-green"
              required
            />
          </motion.div>

          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
          >
            <label
              htmlFor="description"
              className="flex items-center gap-2 text-sm font-medium text-forest-green"
            >
              <FileText className="h-4 w-4" />
              Short Description
            </label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Write a brief and compelling description (2-3 lines)"
              className="border-misty-sage focus:border-forest-green transition-colors duration-300 resize-none placeholder:text-forest-green/30 text-forest-green"
              rows={3}
              required
            />
          </motion.div>

          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.5 }}
          >
            <label
              htmlFor="author"
              className="flex items-center gap-2 text-sm font-medium text-forest-green"
            >
              <User className="h-4 w-4" />
              Author Name
            </label>
            <Input
              id="author"
              value={formData.author}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, author: e.target.value }))
              }
              placeholder="Enter the author's name"
              className="border-misty-sage focus:border-forest-green transition-colors duration-300 placeholder:text-forest-green/30 text-forest-green"
              required
            />
          </motion.div>

          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.55 }}
          >
            <label
              htmlFor="publishDate"
              className="flex items-center gap-2 text-sm font-medium text-forest-green"
            >
              <Calendar className="h-4 w-4" />
              Publish Date
            </label>
            <Input
              id="publishDate"
              type="date"
              value={formData.publishDate || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  publishDate: e.target.value,
                }))
              }
              className="border-misty-sage focus:border-forest-green transition-colors duration-300 text-forest-green"
              required
            />
          </motion.div>

          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.65 }}
          >
            <label className="flex items-center gap-2 text-sm font-medium text-forest-green">
              <PenLine className="h-4 w-4" />
              Content
            </label>
            <div className="rounded-lg border border-misty-sage focus-within:border-forest-green transition-colors duration-300">
              <EditorWrapper
                initialValue={formData.contentHtml}
                onChange={handleEditorChange}
                height="500px"
              />
            </div>
          </motion.div>
        </div>

        <motion.div
          className="flex justify-end pt-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.75 }}
        >
          <Button
            type="submit"
            className="bg-forest-green hover:bg-forest-green/90 transition-colors duration-300 min-w-[150px] h-11"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </motion.div>
      </motion.form>
    </motion.div>
  );
};

export default EditBlog;
